import { useEffect, useRef, useState, type ReactNode } from 'react';

interface DeferUntilNearProps {
  /** Content that is only mounted once the placeholder approaches the viewport. */
  children: ReactNode;
  /** Reserved height while the content is not mounted yet (prevents layout shift). */
  minHeight: number;
  /** Distance from the viewport at which mounting starts. */
  rootMargin?: string;
  /** Optional callback fired once, right before the content mounts. */
  onActivate?: () => void;
}

/**
 * Mounts heavy, non-essential below-the-fold sections only when the visitor
 * approaches them. SEO-relevant static content must NOT be wrapped in this —
 * text and internal links may never depend on scrolling.
 *
 * Falls back to mounting immediately when IntersectionObserver is unavailable.
 */
export function DeferUntilNear({
  children,
  minHeight,
  rootMargin = '200px 0px',
  onActivate,
}: DeferUntilNearProps) {
  const [show, setShow] = useState(false);
  const placeholderRef = useRef<HTMLDivElement | null>(null);
  const activateRef = useRef(onActivate);
  activateRef.current = onActivate;

  useEffect(() => {
    if (show) return;

    if (typeof IntersectionObserver === 'undefined') {
      activateRef.current?.();
      setShow(true);
      return;
    }

    const node = placeholderRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          activateRef.current?.();
          setShow(true);
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [show, rootMargin]);

  if (show) return <>{children}</>;

  return <div ref={placeholderRef} aria-hidden="true" style={{ minHeight }} />;
}
